// `/watch-cpu` — two machines playing, and a record of why.
//
// **A page rather than a mode**, and the difference is what it opens on: every
// other way into a board asks something first (which board, which armies, which
// difficulty), and this one answers all of it from a seed and starts. What it is
// for is watching the opponent think, so the sidebar is the feature and the
// board is the evidence.
//
// The model is in `watchCpu.ts` and is tested; this file is timing, layout and
// a canvas. The split is `celebrations.ts` against `Celebration.tsx`.
//
// **The layout was `WatchCpuPage.css` and is `styled()` here now** (PLAN-UI
// §28.0, priority 2): every colour is a token, the two armies' are `armyTone`,
// and the sidebar's chips, transport and move list are the kit's own
// primitives. Nothing about the page's shape moved.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { applyCommand, type Command, type GameState, newGame } from "src/engine/rules/commands";
import { freshSeed } from "src/engine/game/seed";
import { GameStore } from "src/engine/game/GameStore";
import { GameStoreContext, useGameSnapshot, useStore } from "src/client/ai/hooks/useGameStore";
import type { PlanReport } from "src/api";
import { createGame, gameOf } from "src/engine/api/createGame";
import { BoardCanvas } from "./BoardCanvas";
import { armyTone } from "src/engine/session/armyName";
import {
  Btn,
  HoverCard,
  Link,
  Page,
  Pill,
  PillLabel,
  Segmented,
  SectionTitle,
  StatCounter,
  Text,
  motion,
  styled
} from "./kit";
import {
  forkAt,
  matchupFor,
  openingFor,
  outcomeOf,
  recordOf,
  stateAt,
  tallyOf,
  type MoveRecord
} from "./watchCpu";

/** How fast the machines play, as the watch transport already words it. */
const SPEEDS = [1, 2, 4] as const;

/** The sidebar's width. This page's shape, not the language's. */
const SIDE_WIDTH = "minmax(300px, 25vw)";
/** The banner's height. **Fixed, never `auto`** — see `Shell`. */
const BANNER = 46;
/** Below this the sidebar goes under the board rather than squeezing it. */
const NARROW = 820;

/**
 * **The two sides' scores are not comparable, and the column has to say so.**
 *
 * `valueOfTurn` scores the board from the *mover's* own side, under that army's
 * own weights — so a column of White at +1800 and Black at −300 reads as White
 * crushing, when what it actually says is that two different evaluations were
 * asked two different questions. Measured on the first game this page played:
 * exactly that shape, on a board where material was level.
 *
 * A sentence rather than a normalisation: flipping Black's sign would imply one
 * scale, which is the same false claim more confidently made.
 */
const SCORE_HELP =
  "What this army's own judge scored the board at afterwards, from its own point of view. " +
  "The two sides use different evaluations, so their numbers are not comparable with each other.";

/**
 * A stance is a category, so it is a colour rather than a magnitude — nothing
 * here means "more". The five tones are the language's whole palette for ink,
 * which is why the map is onto them rather than onto six invented hues.
 */
type StanceTone = "ink" | "dim" | "brand" | "cream" | "rust";

const STANCE_TONE: Record<string, StanceTone> = {
  fight: "rust",
  work: "cream",
  balanced: "brand",
  follow: "ink",
  patrol: "brand",
  hunt: "brand"
};

const stanceTone = (name: string): StanceTone => STANCE_TONE[name] ?? "dim";

/**
 * **The sidebar is a fixed column and the board takes the rest**, which is the
 * layout rule this app already keeps: nothing about a selection or a landing
 * move may reflow the canvas, because a canvas that resizes reallocates its
 * drawing buffer and flashes black. So the grid is two tracks and neither
 * depends on content — and the banner row is a fixed height for the same
 * reason.
 */
const Shell = styled(Page)({
  position: "fixed",
  inset: 0,
  minHeight: 0,
  overflow: "hidden",
  display: "grid",
  gridTemplateColumns: `1fr ${SIDE_WIDTH}`,
  gridTemplateRows: `${BANNER}px 1fr`,
  [`@media (max-width: ${NARROW}px)`]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: `${BANNER}px 50vh 1fr`
  }
});

/** Spans both columns: it is about the page, not about the board or the list. */
const Banner = styled("header")(({ theme }) => ({
  gridColumn: "1 / -1",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.kit.space.lg,
  padding: `0 ${theme.kit.space.lg}px`,
  background: theme.kit.color.panel,
  borderBottom: `2px solid ${theme.kit.color.line}`,
  overflow: "hidden"
}));

const BannerWhat = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  gap: theme.kit.space.md,
  minWidth: 0
}));

/** The sentence is the first thing to go on a narrow window. */
const BannerDetail = styled(Text)(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [`@media (max-width: ${theme.kit.breakpoint.tablet}px)`]: { display: "none" }
}));

const BoardArea = styled("div")({ position: "relative", minWidth: 0, minHeight: 0 });

const Scrubbed = styled(Pill)({
  position: "absolute",
  top: 12,
  left: "50%",
  transform: "translateX(-50%)",
  whiteSpace: "nowrap",
  pointerEvents: "none"
});

const Side = styled("aside")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  background: theme.kit.color.panel,
  borderLeft: `2px solid ${theme.kit.color.line}`,
  [`@media (max-width: ${NARROW}px)`]: {
    borderLeft: 0,
    borderTop: `2px solid ${theme.kit.color.line}`
  }
}));

const Head = styled("div")(({ theme }) => ({
  padding: `${theme.kit.space.md}px ${theme.kit.space.lg}px ${theme.kit.space.sm}px`,
  borderBottom: `2px solid ${theme.kit.color.line}`
}));

/** The two army names, and the word between them. */
const Armies = styled("p")(({ theme }) => ({
  margin: `${theme.kit.space.xs}px 0 2px`,
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 18
}));

const Group = styled("div")(({ theme }) => ({
  padding: `${theme.kit.space.sm}px ${theme.kit.space.lg}px`,
  borderBottom: `2px solid ${theme.kit.color.line}`,
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.kit.space.sm
}));

/**
 * An ended game and a broken page look identical to a spectator, so the result
 * is loud and sits above the numbers rather than among them.
 *
 * Not a kit `Notice`: it is a strip across the sidebar with the rest of the
 * column's rules, not a block inside a page of prose.
 */
const Outcome = styled("p", {
  shouldForwardProp: (prop: string) => prop !== "over"
})<{ over?: boolean }>(({ theme, over }) => {
  const rule = over ? theme.kit.color.brand : theme.kit.color.cream;
  return {
    margin: 0,
    padding: `${theme.kit.space.sm}px ${theme.kit.space.lg}px`,
    fontFamily: theme.kit.font.body,
    fontSize: 13,
    fontWeight: over ? 700 : 400,
    color: rule,
    background: `color-mix(in srgb, ${rule} 12%, ${theme.kit.color.panel})`,
    borderBottom: `2px solid ${theme.kit.color.line}`
  };
});

/** Numbers first: the move list below is the evidence under them. */
const Stats = styled("section")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 2,
  background: theme.kit.color.line,
  borderBottom: `2px solid ${theme.kit.color.line}`
}));

const StatCell = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: `${theme.kit.space.sm}px ${theme.kit.space.md}px`,
  background: theme.kit.color.panel
}));

/**
 * A stance, as a chip. `Pill` states a fact and is never a control.
 *
 * The category's colour is a `TextTone`, resolved here rather than passed as a
 * colour: the five tones are the whole palette ink comes in, and a chip that
 * took any colour would be the sixth voice on the page.
 */
const Chip = styled(Pill, {
  shouldForwardProp: (prop: string) => prop !== "tone"
})<{ tone: StanceTone }>(({ theme, tone }) => {
  const c = theme.kit.color;
  const ink = { ink: c.ink, dim: c.inkDim, brand: c.brand, cream: c.cream, rust: c.rust }[tone];
  return {
    padding: "1px 8px",
    fontFamily: theme.kit.font.display,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: ".05em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    color: ink,
    borderWidth: 1.5,
    borderColor: `color-mix(in srgb, ${ink} 45%, transparent)`,
    background: `color-mix(in srgb, ${ink} 14%, transparent)`,
    backdropFilter: "none"
  };
});

const Moves = styled("ol")(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  margin: 0,
  padding: `${theme.kit.space.xs}px 0 ${theme.kit.space.xl}px`,
  listStyle: "none"
}));

const MoveRow = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "shown" && prop !== "fallback"
})<{ shown?: boolean; fallback?: boolean }>(({ theme, shown }) => ({
  display: "grid",
  gridTemplateColumns: "26px 8px 1fr auto auto auto",
  alignItems: "center",
  gap: 7,
  width: "100%",
  padding: `5px ${theme.kit.space.md}px`,
  background: shown
    ? `color-mix(in srgb, ${theme.kit.color.brand} 12%, ${theme.kit.color.panel})`
    : "transparent",
  border: 0,
  borderLeft: `2px solid ${shown ? theme.kit.color.brand : "transparent"}`,
  color: theme.kit.color.ink,
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  textAlign: "left",
  cursor: "pointer",
  ...motion([], ["background"]),
  "&:hover": {
    background: `color-mix(in srgb, ${theme.kit.color.panelRaised} 70%, transparent)`
  }
}));

/** A fallback is the planner having failed rather than decided. */
const Label = styled("span", {
  shouldForwardProp: (prop: string) => prop !== "fallback"
})<{ fallback?: boolean }>(({ theme, fallback }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: fallback ? theme.kit.color.cream : theme.kit.color.ink
}));

/** Whose move it was, in that army's own colour (`armyTone`). */
const Dot = styled("span", {
  shouldForwardProp: (prop: string) => prop !== "tone"
})<{ tone: string }>(({ tone }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: tone
}));

/** The way back into the game, in the banner. `Btn`'s plate, as an anchor. */
const PlayLink = styled(Btn.withComponent("a"))({ flex: "none", textDecoration: "none" });

/** A stat's number. Ink, not the brand — six loud numbers is no emphasis. */
const StatValue = styled(StatCounter)(({ theme }) => ({
  color: theme.kit.color.ink,
  fontSize: 18
}));

export function WatchCpuPage({ onClose }: { onClose: () => void }) {
  // **The seed is read once, from the address if it is there.** A shared link
  // has to be the same game — the seed rides in the opening command, so the
  // play replays, and `matchupFor` makes the *setup* replay with it.
  const [seed] = useState(() => {
    const asked = Number(new URLSearchParams(window.location.search).get("seed"));
    return Number.isFinite(asked) && asked > 0 ? asked : freshSeed();
  });
  // One store for this page, never the app's. A spectator's game must not be
  // able to touch a run in progress — the same carve-out `watch-run` makes with
  // `ownRun`, arrived at from the other side.
  const [store] = useState(() => new GameStore());

  return (
    <GameStoreContext.Provider value={store}>
      <WatchCpuBoard seed={seed} onClose={onClose} />
    </GameStoreContext.Provider>
  );
}

function WatchCpuBoard({ seed, onClose }: { seed: number; onClose: () => void }) {
  const store = useStore();
  const { state, log } = useGameSnapshot();
  const matchup = useMemo(() => matchupFor(seed), [seed]);

  const [records, setRecords] = useState<MoveRecord[]>([]);
  const [paused, setPaused] = useState(false);
  const [step, setStep] = useState(0);
  const [speed, setSpeed] = useState<number>(2);
  /** Which entry the board is showing; null is "keep up with the game". */
  const [review, setReview] = useState<number | null>(null);

  // **Opened once, and latched.** StrictMode mounts, cleans up and mounts
  // again, so an unlatched opening dispatches `new-game` twice — which is not a
  // no-op, it is a second board with the first one's records still beside it.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    store.dispatch(openingFor(matchup));
  }, [store, matchup, seed]);

  /**
   * The plan waiting to be attached to the move it produced.
   *
   * A turn is dispatched free-commands-first with the move last, so the report
   * arrives before any of them and is claimed by the next entry the list gains.
   * A ref rather than state: it is claimed inside a store subscription, and a
   * setState there would re-render once per command for a value nothing draws.
   */
  const pending = useRef<PlanReport | null>(null);
  const onPlan = useCallback((report: PlanReport) => {
    pending.current = report;
  }, []);

  /**
   * Build the list as the game plays, from the state we already had.
   *
   * **Subscribed to the store directly rather than to React's snapshot**, for
   * one reason: `dispatch` notifies per command, and a turn is several. React
   * may coalesce those into one render, and a list built from renders would
   * miss the board *before* each move — which is where a capture's victim and
   * the mover's own rank are still readable. Holding the previous snapshot
   * makes `before` free; the alternative is re-applying every command a second
   * time, which doubles the cost of the game to describe it.
   */
  const previous = useRef<{ state: GameState; length: number }>({
    state: store.getSnapshot().state,
    length: store.getSnapshot().log.length
  });
  useEffect(() => {
    return store.subscribe(() => {
      const snapshot = store.getSnapshot();
      const seen = previous.current;
      // A fork truncates, so the log can *shrink*. Rebuilding the tail is
      // wrong here — the records for what remains are already right — so the
      // cursor is reset and the list is trimmed where the fork happened.
      if (snapshot.log.length < seen.length) {
        previous.current = { state: snapshot.state, length: snapshot.log.length };
        return;
      }
      let before = seen.state;
      const gained: MoveRecord[] = [];
      for (let i = seen.length; i < snapshot.log.length; i += 1) {
        const command = snapshot.log[i];
        // The last command of a turn is the one that spends it, so `before` is
        // the board this command actually saw.
        const after =
          i === snapshot.log.length - 1 ? snapshot.state : applyCommand(before, command);
        const report = pending.current;
        const record = recordOf(before, after, command, i, 0, {
          stance: report?.stance,
          value: report?.value,
          fallback: report?.fallback,
          free: Math.max(0, (report?.commands ?? 1) - 1)
        });
        if (record) {
          gained.push(record);
          // Claimed, so the next turn's entry does not inherit this one's
          // reasoning — which would read as the planner repeating itself.
          pending.current = null;
        }
        before = after;
      }
      previous.current = { state: snapshot.state, length: snapshot.log.length };
      if (gained.length > 0) {
        // Numbered here rather than in the model, because the number is a
        // property of the list and the model is handed one entry at a time.
        setRecords((held) => [
          ...held,
          ...gained.map((record, at) => ({ ...record, number: held.length + at + 1 }))
        ]);
      }
    });
  }, [store]);

  // What the board draws while it plays: its own `Game` over this page's
  // store, and the drivers live on it (`game.drivers`).
  const liveGame = useMemo(() => createGame(store), [store]);
  useEffect(() => () => liveGame.dispose(), [liveGame]);
  // Every side but nobody's — watch mode, which is what plays both armies.
  // Profiles unset on purpose: each army takes its evaluation from the game.
  useEffect(() => {
    liveGame.drivers.configure({
      enabled: true,
      playerSide: null,
      level: "normal",
      pace: speed,
      paused,
      step
    });
  }, [liveGame, speed, paused, step]);
  useEffect(() => liveGame.on("plan", onPlan), [liveGame, onPlan]);

  /**
   * The board being shown — the live one, or a replay of a prefix.
   *
   * Memoised on the index rather than on the log, or every command the game
   * plays would replay the whole game underneath a paused spectator.
   * `newGame()` is the right base because the first entry in this log is the
   * `new-game` that opened the scenario.
   */
  const shown = useMemo(() => {
    if (review === null) return state;
    return stateAt(newGame(), log, review);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review, review === null ? state : null]);
  // What the board draws: the live game while it plays, a still board under
  // review — its own `Game`, so scrubbing never animates a move as played.
  const game = useMemo(() => (review === null ? liveGame : gameOf(shown)), [review, liveGame, shown]);

  const tally = useMemo(() => tallyOf(records, shown.position.sides ?? 2), [records, shown]);

  /** Replay from the entry on screen, discarding everything after it. */
  const replayFrom = useCallback(
    (at: number) => {
      const kept = forkAt(log, at);
      setRecords((held) => held.filter((record) => record.at <= at));
      setReview(null);
      store.load(kept as Command[]);
    },
    [log, store]
  );

  const live = review === null;
  const reviewing = review !== null ? records.find((r) => r.at === review) : undefined;
  // **Read off the live game, never off the board being reviewed.** Scrubbing
  // back to move 5 of a finished game must still say the game is over — the
  // outcome is a fact about the match, and hiding it while looking at history
  // is how a spectator concludes the page has stopped working.
  const outcome = outcomeOf(state.status, state.position.turn, matchup.names);

  return (
    <Shell>
      {/*
       * **The header is identity, which is the layout rule this app already
       * keeps.** Somebody arriving here from a link has no way to tell a
       * demonstration from the game itself — the board is the real board, the
       * armies are the real armies, and nothing on the page was saying so or
       * offering a way in. It spans both columns because it is about the page
       * rather than about the board or the list.
       */}
      <Banner>
        <BannerWhat>
          {/* The space is real, not the flex gap: a screen reader reads the
           * text, and without it the label runs into the sentence. */}
          <SectionTitle>Opponent preview</SectionTitle>{" "}
          <BannerDetail tone="dim" size={12}>
            Two armies of Factory Chess, playing each other on a fair board.
          </BannerDetail>
        </BannerWhat>
        {/*
         * A real `<a href>` so it can be middle-clicked, opened in a tab and
         * read by anything that reads links — and `onClose` on top of it, which
         * is the same in-app hand-back the route already does rather than a
         * reload. Both, because either alone is worse: a button is not a link,
         * and a bare link throws away the app that is already running.
         */}
        <PlayLink
          href="/"
          data-testid="watchcpu-play"
          onClick={(event) => {
            // Let the browser have the modified clicks — a new tab is exactly
            // what somebody means by ctrl-clicking a link.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            onClose();
          }}
        >
          Play Factory Chess →
        </PlayLink>
      </Banner>

      <BoardArea>
        {/*
         * **Every interaction handler is a no-op, and that is the page.** A
         * spectator has no seat, so a click that selected a piece would offer
         * moves nobody may play — the silent no-op `useBoardInteraction` exists
         * to prevent, arriving from the other direction. The camera still turns
         * and zooms, because those are about the viewer's eye rather than the
         * game. Board `paceScale` follows the transport while the game is live
         * and drops to 1 under review, where nothing is being played.
         */}
        <BoardCanvas
          game={game}
          interactive={false}
          paceScale={live ? speed : 1}
        />
        {!live && (
          <Scrubbed role="status">
            Reviewing move {reviewing?.number ?? "—"} — the game is still going
          </Scrubbed>
        )}
      </BoardArea>

      <Side>
        <Head>
          <SectionTitle>Watching</SectionTitle>
          {/* Spaces are real, not margins: the margin is what a sighted reader
           * sees and a screen reader reads the text, which ran the two army
           * names together as one word. */}
          <Armies>
            <span style={{ color: armyTone(0) }}>{matchup.names[0]}</span>{" "}
            <Text tone="dim" size={13}>
              vs
            </Text>{" "}
            <span style={{ color: armyTone(1) }}>{matchup.names[1]}</span>
          </Armies>
          <HoverCard
            placement="bottom-start"
            card="A rotationally symmetric board: the same ground, ore and ingots for each army, seen from the other end."
          >
            <Text tone="dim" size={12} block>
              Fair board {matchup.board.size}×{matchup.board.size} · seed {seed}
            </Text>
          </HoverCard>
        </Head>

        {outcome && (
          <Outcome over={outcome.over} role="status" data-testid="watchcpu-outcome">
            {outcome.text}
            {outcome.over && (
              <>
                {" "}
                <Link href="/watch-cpu">Watch another</Link>
              </>
            )}
          </Outcome>
        )}

        <Stats>
          <Stat label="Turn" value={tally.turns || shown.position.fullmove} />
          <Stat label="Moves" value={tally.moves} />
          <Stat label={`${matchup.names[0]} took`} value={tally.taken[0] ?? 0} />
          <Stat label={`${matchup.names[1]} took`} value={tally.taken[1] ?? 0} />
          <Stat label="Losses" value={`${tally.losses[0] ?? 0} – ${tally.losses[1] ?? 0}`} />
          <Stat
            label="Fallbacks"
            value={tally.fallbacks}
            hint="Turns the planner missed its deadline on and played a simple move instead."
          />
        </Stats>

        {tally.stances.length > 0 && (
          <Group>
            <SectionTitle style={{ width: "100%" }}>How turns were chosen</SectionTitle>
            {tally.stances.map((stance) => (
              <span key={stance.name} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Stance name={stance.name} />
                <Text role="display" tone="dim" size={12}>
                  {stance.count}
                </Text>
              </span>
            ))}
          </Group>
        )}

        <Group role="group" aria-label="Playback">
          <Btn small onClick={() => setPaused((was) => !was)} data-testid="watchcpu-playpause">
            {paused ? "▶ Play" : "❚❚ Pause"}
          </Btn>
          <HoverCard placement="bottom" card="Play exactly one more turn.">
            <Btn
              small
              disabled={!paused}
              onClick={() => setStep((n) => n + 1)}
              data-testid="watchcpu-step"
            >
              ▸❙ Step
            </Btn>
          </HoverCard>
          <Segmented
            small
            style={{ marginLeft: "auto" }}
            data-testid="watchcpu-speed"
            aria-label="Speed"
            value={speed}
            onChange={setSpeed}
            options={SPEEDS.map((option) => ({
              value: option as number,
              label: `${option}×`,
              testId: `watchcpu-speed-${option}`
            }))}
          />
        </Group>

        <Group>
          <Btn small disabled={live} onClick={() => setReview(null)} data-testid="watchcpu-live">
            Continue from latest
          </Btn>
          <HoverCard placement="bottom" card="Discard everything after this move and play on from it.">
            <Btn
              small
              disabled={live}
              onClick={() => review !== null && replayFrom(review)}
              data-testid="watchcpu-replay"
            >
              Replay from here
            </Btn>
          </HoverCard>
        </Group>

        <Moves data-testid="watchcpu-moves">
          {records.map((record) => (
            <li key={record.at}>
              <MoveRow
                type="button"
                shown={review === record.at}
                onClick={() => setReview(record.at)}
              >
                <Text role="display" tone="dim" size={11} style={{ textAlign: "right" }}>
                  {record.number}
                </Text>
                <Dot tone={armyTone(record.side)} aria-hidden="true" />
                <Label fallback={record.fallback}>{record.label}</Label>
                {record.captured ? (
                  <Text role="display" tone="cream" size={11}>
                    +{record.captured.value}
                  </Text>
                ) : (
                  <span />
                )}
                {record.stance ? (
                  <Stance name={record.stance} />
                ) : (
                  <Chip tone="dim">—</Chip>
                )}
                {record.value !== undefined ? (
                  <HoverCard placement="left" card={SCORE_HELP}>
                    <Text
                      role="display"
                      tone="dim"
                      size={11}
                      style={{ minWidth: 42, textAlign: "right", display: "inline-block" }}
                    >
                      {record.value > 0 ? "+" : ""}
                      {Math.round(record.value)}
                    </Text>
                  </HoverCard>
                ) : (
                  <span />
                )}
              </MoveRow>
            </li>
          ))}
          {records.length === 0 && (
            <li style={{ padding: "14px 16px" }}>
              <Text tone="dim" size={12}>
                Waiting for the first turn…
              </Text>
            </li>
          )}
        </Moves>
      </Side>
    </Shell>
  );
}

/** One stance chip, in the tone its category was given. */
function Stance({ name }: { name: string }) {
  return <Chip tone={stanceTone(name)}>{name}</Chip>;
}

function Stat({
  label,
  value,
  hint
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  const cell = (
    <StatCell>
      <StatValue>{value}</StatValue>
      <PillLabel>{label}</PillLabel>
    </StatCell>
  );
  return hint ? (
    <HoverCard placement="left" card={hint}>
      {cell}
    </HoverCard>
  ) : (
    cell
  );
}
